import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import cn from "classnames";

import { ReactComponent as SearchIcon } from "../../../media/icons/search.svg";
import { ReactComponent as LocationIcon } from "../../../media/icons/location.svg";
import { ReactComponent as ClockIcon } from "../../../media/icons/clock.svg";
import { ReactComponent as CloseIcon } from "../../../media/icons/close-cross-thin.svg";
import { feedReturnState } from "../../../helpers/feedReturn";
import { trackDataLayer } from "../../../helpers/track";
import { eventNameDataLayer } from "../../../constants/track";
import { JOB_TYPE_LABEL_KEYS, eventStart, formatLocation } from "../../feed/components/feedCard";
import { clearRecent, getRecent, getSession, pushRecent, saveSession } from "./searchStorage";
import { useSuggest } from "./useSuggest";
import { useFocusTrap } from "../../../helpers/hooks";
import { useIsMobile } from "./useIsMobile";
import styles from "./mainSearchV2.module.css";

/** The scopes, in chip order. The group types of the answer are the same names. */
export const SCOPES = ["all", "profile", "organization", "event", "job", "community"];

const BROWSE_SCOPES = SCOPES.filter(scope => scope !== "all");
const DEBOUNCE_MS = 200;
const LISTING_TYPES = ["profile", "organization", "event", "job"];

const scopeLabelKey = scope => `dashboard_header_search_scope_${scope}`;

/** "Houston, TX" from the member's profile, or null when it has no city. */
const profileCity = profile => (profile.city ? String(profile.city) : null);
const profileLocationLabel = profile => [profile.city, profile.state].filter(Boolean).join(", ");

/**
 * The one-line meta under a row's title, worded through t() from the raw fields the API sends.
 *
 * @param {Object} item a suggest item
 * @param {Function} t
 * @returns {string}
 */
export const metaLine = (item, t) => {
  const meta = item.meta || {};
  const location = formatLocation(meta.location);
  const parts = [];

  switch (item.type) {
    case "profile":
      parts.push((meta.practice_areas || []).join(", "), location);
      if (meta.sponsored) parts.push(t("dashboard_header_search_sponsor"));
      break;
    case "organization":
      parts.push(location, meta.summary);
      break;
    case "event": {
      const startsAt = eventStart({ starts_at: meta.starts_at });
      parts.push(startsAt ? format(startsAt, "EEE, MMM d · h:mm a") : null, location);
      break;
    }
    case "job":
      parts.push(
        meta.company,
        location,
        (meta.job_type_ids || [])
          .map(id => JOB_TYPE_LABEL_KEYS[id])
          .filter(Boolean)
          .map(key => t(key))
          .join(", ")
      );
      break;
    default:
      parts.push(
        meta.following ? t("dashboard_header_search_following") : null,
        meta.member_count ? t("dashboard_header_search_members", { count: meta.member_count }) : null
      );
  }

  return parts.filter(Boolean).join(" · ");
};

const initialsOf = title =>
  String(title || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(word => word[0])
    .join("")
    .toUpperCase();

/**
 * The header search (GROWTH-7577, behind HEADER_SEARCH_V2_ENABLED): one typeahead over people, organizations,
 * events, jobs and communities, with the city as a refinement and results that open inside the dashboard.
 * Enter or the footer applies the text to My Feed (`/?q=…&type=…`); a row opens its own page.
 *
 * @param {Object} props
 * @param {string} [props.className] the header's slot class (hidden below 768px)
 */
const MainSearchV2 = ({ className }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const profile = useSelector(state => state.account.profile);
  const { login } = profile;
  const panelId = useId();
  const listboxId = `${panelId}-results`;
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const triggerRef = useRef(null);

  const session = useMemo(() => getSession(), []);
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("main");
  const [scope, setScope] = useState(SCOPES.includes(session.scope) ? session.scope : "all");
  // The member's own choice this session ({city} or {city: null} for Anywhere), or null to follow the profile. The
  // header renders before the profile loads, so the profile city is read on every render rather than once.
  const [chosenLocation, setChosenLocation] = useState(() => {
    if (session.location === "anywhere") return { city: null };
    if (session.location && session.location.city) return { city: session.location.city };
    return null;
  });
  const city = chosenLocation ? chosenLocation.city : profileCity(profile);
  const [recent, setRecent] = useState(() => getRecent(login));
  const [activeIndex, setActiveIndex] = useState(-1);
  const scrollActiveIntoView = useRef(false);
  // Below 768px the search is an icon button that opens the same panel full-screen (GROWTH-7577).
  const isMobile = useIsMobile();
  const [mobileOpen, setMobileOpen] = useState(false);
  const wasMobileOpen = useRef(false);
  const isFullScreen = isMobile && mobileOpen;

  const trimmed = query.trim();

  useEffect(() => {
    setRecent(getRecent(login));
  }, [login]);
  // A Browse chip with an empty box lists that scope's newest (GROWTH-7646), only while the panel is open.
  const browsing = open && view === "main" && scope !== "all" && trimmed === "" && debouncedQuery === "";
  const { status, data } = useSuggest(debouncedQuery, scope, city, browsing);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(trimmed), trimmed === "" ? 0 : DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [trimmed]);

  useEffect(() => {
    if (debouncedQuery !== "") {
      trackDataLayer(eventNameDataLayer.headerSearchQuery, {
        len: debouncedQuery.length,
        scope,
        location: city ? "city" : "anywhere"
      });
    }
  }, [debouncedQuery, scope, city]);

  useEffect(() => {
    if (!open) return undefined;

    const handleOutside = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) closePanel();
    };
    document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open]);

  useEffect(() => {
    setActiveIndex(-1);
  }, [data, scope, view, open]);

  useEffect(() => {
    // Widening past the phone range while the full-screen panel is open puts the search back in the bar.
    if (!isMobile && mobileOpen) setMobileOpen(false);
  }, [isMobile, mobileOpen]);

  useEffect(() => {
    if (!isFullScreen) return undefined;

    if (inputRef.current) inputRef.current.focus();
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isFullScreen]);

  // Tab and Shift+Tab cycle inside the dialog, and the page behind is inert, so neither the keyboard nor a screen
  // reader reaches it (GROWTH-7647). `inert` goes on every sibling along the path from the dialog up to <body>.
  useFocusTrap(containerRef, isFullScreen);
  useEffect(() => {
    if (!isFullScreen || !containerRef.current) return undefined;

    const made = [];
    for (let node = containerRef.current; node && node !== document.body; node = node.parentElement) {
      if (!node.parentElement) break;
      Array.from(node.parentElement.children).forEach(sibling => {
        if (sibling !== node && !sibling.hasAttribute("inert")) {
          sibling.setAttribute("inert", "");
          made.push(sibling);
        }
      });
    }
    return () => made.forEach(sibling => sibling.removeAttribute("inert"));
  }, [isFullScreen]);

  // Escape closes the dialog from any control in it, not just the input (the input's own handler also blurs it).
  useEffect(() => {
    const dialogElement = containerRef.current;
    if (!isFullScreen || !dialogElement) return undefined;

    const handleEscape = event => {
      if (event.key === "Escape" && event.target !== inputRef.current) closePanel();
    };
    dialogElement.addEventListener("keydown", handleEscape);
    return () => dialogElement.removeEventListener("keydown", handleEscape);
  }, [isFullScreen]); // eslint-disable-line react-hooks/exhaustive-deps -- closePanel only sets state, so the first render's copy is fine

  useEffect(() => {
    // Closing returns focus to the icon that opened the panel.
    if (wasMobileOpen.current && !mobileOpen && triggerRef.current) triggerRef.current.focus();
    wasMobileOpen.current = mobileOpen;
  }, [mobileOpen]);

  // The listbox's options in DOM order: each group's "See all" (when it has more than shown), its rows, then the
  // footer. A listbox may only hold options, so "See all" is one too, reachable by the arrows like the footer.
  // Browsing has no text to apply, so no footer; the chip already is the scope, so no "See all".
  const options = useMemo(() => {
    const flat = [];
    ((data && data.groups) || []).forEach(group => {
      if (!browsing && group.total > group.items.length) flat.push({ kind: "seeAll", group: group.type, total: group.total });
      group.items.forEach((item, position) => flat.push({ kind: "row", item, group: group.type, position }));
    });
    if (flat.length > 0 && !browsing) flat.push({ kind: "footer" });
    return flat;
  }, [data, browsing]);

  const counts = (data && data.counts) || null;
  const showResults = (browsing || (trimmed !== "" && debouncedQuery !== "")) && data !== null;
  const hasRows = options.some(option => option.kind === "row");
  const optionCount = options.length;
  const optionId = index => `${panelId}-option-${index}`;

  useEffect(() => {
    if (!scrollActiveIntoView.current || activeIndex < 0) return;
    scrollActiveIntoView.current = false;
    const element = document.getElementById(optionId(activeIndex));
    if (element && element.scrollIntoView) element.scrollIntoView({ block: "nearest" });
  }, [activeIndex]); // eslint-disable-line react-hooks/exhaustive-deps
  const locationLabel = city === null ? null : city === profileCity(profile) ? profileLocationLabel(profile) : city;

  const closePanel = () => {
    setOpen(false);
    setMobileOpen(false);
  };

  const openPanel = () => {
    if (!open) {
      setOpen(true);
      trackDataLayer(eventNameDataLayer.headerSearchOpen, {});
    }
  };

  const openMobile = () => {
    setMobileOpen(true);
    openPanel();
  };

  const remember = text => setRecent(pushRecent(login, text));

  const chooseScope = next => {
    setScope(next);
    saveSession({ scope: next });
    if (inputRef.current) inputRef.current.focus();
  };

  const chooseCity = (nextCity, mode) => {
    setChosenLocation({ city: nextCity });
    saveSession({ location: mode === "anywhere" ? "anywhere" : { city: nextCity }, city: nextCity || undefined });
    setView("main");
    if (inputRef.current) inputRef.current.focus();
  };

  const resultCount = () => {
    if (!counts) return 0;
    if (scope === "all") return LISTING_TYPES.concat("community").reduce((sum, type) => sum + (counts[type] || 0), 0);
    return counts[scope] || 0;
  };

  const apply = text => {
    const applied = (text || trimmed).trim();
    if (applied === "") return;

    remember(applied);
    trackDataLayer(eventNameDataLayer.headerSearchApply, { scope, resultCount: resultCount() });
    const params = new URLSearchParams({ q: applied });
    if (scope !== "all" && scope !== "community") params.set("type", scope);
    closePanel();
    navigate(`/?${params.toString()}`);
  };

  // What a listing row carries to its page, for the "My Feed" breadcrumb (GROWTH-7645): back to the results of the text the
  // rows answer, with the type Enter would have applied. A community row leaves the dashboard's listing pages, so it carries none.
  const rowLinkState = item =>
    LISTING_TYPES.includes(item.type)
      ? feedReturnState({ type: LISTING_TYPES.includes(scope) ? scope : null, q: debouncedQuery })
      : undefined;

  const select = (row, viaKeyboard) => {
    remember(trimmed);
    trackDataLayer(eventNameDataLayer.headerSearchSelect, { type: row.item.type, position: row.position, group: row.group });
    closePanel();
    if (viaKeyboard) navigate(row.item.href, { state: rowLinkState(row.item) });
  };

  const handleKeyDown = event => {
    if (event.key === "Escape") {
      closePanel();
      event.currentTarget.blur();
      return;
    }

    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      openPanel();
      if (optionCount === 0 || view !== "main") return;
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      scrollActiveIntoView.current = true;
      setActiveIndex(current => {
        if (current === -1) return step === 1 ? 0 : optionCount - 1;
        return (current + step + optionCount) % optionCount;
      });
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const active = activeIndex >= 0 ? options[activeIndex] : null;
      if (active && active.kind === "row") {
        select(active, true);
      } else if (active && active.kind === "seeAll") {
        chooseScope(active.group);
      } else {
        apply();
      }
    }
  };

  // Tab leaving the search closes the panel. A blur with no new focus (a click on something that takes no focus,
  // such as a button in Safari) is left to the click-outside handler, so a click inside keeps the panel open.
  // In the full-screen dialog focus cannot leave (the trap holds it), so a blur never closes it.
  const handleBlur = event => {
    if (isFullScreen) return;
    const next = event.relatedTarget;
    if (next && containerRef.current && !containerRef.current.contains(next)) closePanel();
  };

  const handleClear = () => {
    setQuery("");
    if (inputRef.current) inputRef.current.focus();
  };

  // Hover sets the active option on mousemove, not mouseenter: an arrow key that scrolls the panel under a resting
  // pointer fires mouseenter on the option now beneath it, which would take the active option from the keyboard.
  const renderRow = (row, index) => {
    const { item } = row;
    const meta = metaLine(item, t);
    const isPerson = item.type === "profile";

    return (
      <Link
        key={`${item.type}-${item.id}`}
        id={optionId(index)}
        role="option"
        aria-selected={activeIndex === index}
        to={item.href}
        state={rowLinkState(item)}
        className={cn(styles.row, { [styles.rowActive]: activeIndex === index })}
        onMouseMove={() => setActiveIndex(index)}
        onClick={() => select(row, false)}
      >
        <span className={cn(styles.media, { [styles.mediaPerson]: isPerson })} aria-hidden="true">
          {item.image_url ? <img src={item.image_url} alt="" /> : initialsOf(item.title)}
        </span>
        <span className={styles.rowText}>
          <span className={styles.rowTitle}>{item.title}</span>
          {meta ? <span className={styles.rowMeta}>{meta}</span> : null}
        </span>
        {item.community_name ? <span className={styles.rowCommunity}>{item.community_name}</span> : null}
      </Link>
    );
  };

  const renderSeeAll = (group, index) => (
    <button
      type="button"
      id={optionId(index)}
      role="option"
      aria-selected={activeIndex === index}
      className={cn(styles.seeAll, { [styles.seeAllActive]: activeIndex === index })}
      onMouseMove={() => setActiveIndex(index)}
      onClick={() => chooseScope(group.type)}
    >
      {t("dashboard_header_search_see_all", { count: group.total })}
    </button>
  );

  const renderResults = () => {
    let index = 0;
    const footerIndex = optionCount - 1;

    return (
      <>
        <div className={styles.scopes} role="group" aria-label={t("dashboard_header_search_scopes_label")}>
          {SCOPES.map(item => (
            <button
              key={item}
              type="button"
              className={cn(styles.scopeChip, { [styles.scopeChipActive]: scope === item })}
              aria-pressed={scope === item}
              onClick={() => chooseScope(item)}
            >
              {t(scopeLabelKey(item))}
              {counts ? <span className={styles.scopeCount}>{item === "all" ? resultCountAll(counts) : counts[item] || 0}</span> : null}
            </button>
          ))}
        </div>
        {hasRows ? (
          <div id={listboxId} role="listbox" aria-label={t("dashboard_header_search_label")}>
            {data.groups.map(group => {
              let seeAll = null;
              if (!browsing && group.total > group.items.length) {
                seeAll = renderSeeAll(group, index);
                index += 1;
              }

              return (
                <div key={group.type} role="group" aria-label={t(scopeLabelKey(group.type))}>
                  <div className={styles.groupHeader}>
                    <span>{t(scopeLabelKey(group.type))}</span>
                    {seeAll}
                  </div>
                  {group.items.map(item => {
                    const row = { item, group: group.type, position: group.items.indexOf(item) };
                    const element = renderRow(row, index);
                    index += 1;
                    return element;
                  })}
                </div>
              );
            })}
            {browsing ? null : (
              <button
                type="button"
                id={optionId(footerIndex)}
                role="option"
                aria-selected={activeIndex === footerIndex}
                className={cn(styles.footer, { [styles.rowActive]: activeIndex === footerIndex })}
                onMouseMove={() => setActiveIndex(footerIndex)}
                onClick={() => apply()}
              >
                <span className={styles.footerLabel}>{t("dashboard_header_search_show_all", { query: trimmed })}</span>
                <kbd className={styles.hint} aria-hidden="true">
                  ↵
                </kbd>
              </button>
            )}
          </div>
        ) : (
          <div className={styles.empty}>
            <p className={styles.emptyTitle}>
              {browsing
                ? t("dashboard_header_search_browse_hint", { scope: t(scopeLabelKey(scope)) })
                : t("dashboard_header_search_empty", { query: trimmed })}
            </p>
            {city ? (
              <p className={styles.emptyText}>
                {t("dashboard_header_search_empty_near", { location: locationLabel })}{" "}
                <button type="button" className={styles.link} onClick={() => chooseCity(null, "anywhere")}>
                  {t("dashboard_header_search_empty_anywhere")}
                </button>
              </p>
            ) : null}
          </div>
        )}
      </>
    );
  };

  const renderIdle = () => (
    <>
      {recent.length > 0 ? (
        <div>
          <div className={styles.groupHeader}>
            <span>{t("dashboard_header_search_recent")}</span>
            <button
              type="button"
              className={styles.seeAll}
              onClick={() => {
                clearRecent(login);
                setRecent([]);
              }}
            >
              {t("dashboard_header_search_recent_clear")}
            </button>
          </div>
          {recent.map(item => (
            <button
              key={item}
              type="button"
              className={styles.recentRow}
              onClick={() => {
                setQuery(item);
                if (inputRef.current) inputRef.current.focus();
              }}
            >
              <ClockIcon className={styles.recentIcon} aria-hidden="true" />
              {item}
            </button>
          ))}
        </div>
      ) : null}
      <div className={styles.groupHeader}>
        <span>{t("dashboard_header_search_browse")}</span>
      </div>
      <div className={styles.browse}>
        {BROWSE_SCOPES.map(item => (
          <button
            key={item}
            type="button"
            className={cn(styles.scopeChip, { [styles.scopeChipActive]: scope === item })}
            aria-pressed={scope === item}
            onClick={() => chooseScope(item)}
          >
            {t(scopeLabelKey(item))}
          </button>
        ))}
      </div>
      {scope !== "all" ? (
        <p className={styles.emptyText}>{t("dashboard_header_search_browse_hint", { scope: t(scopeLabelKey(scope)) })}</p>
      ) : null}
    </>
  );

  const renderLocations = () => {
    const own = profileCity(profile);
    const others = session.cities.filter(item => item !== own);

    return (
      <div>
        <div className={styles.groupHeader}>
          <span>{t("dashboard_header_search_near_heading")}</span>
        </div>
        {own ? (
          <button type="button" className={styles.locationRow} onClick={() => chooseCity(own, "city")}>
            <span>{profileLocationLabel(profile)}</span>
            <span className={styles.rowCommunity}>{t("dashboard_header_search_profile_location")}</span>
          </button>
        ) : null}
        {others.map(item => (
          <button key={item} type="button" className={styles.locationRow} onClick={() => chooseCity(item, "city")}>
            {item}
          </button>
        ))}
        <button type="button" className={styles.locationRow} onClick={() => chooseCity(null, "anywhere")}>
          {t("dashboard_header_search_anywhere")}
        </button>
      </div>
    );
  };

  const announcement = (() => {
    if (status === "loading") return t("dashboard_header_search_loading");
    if (status === "ready" && showResults) return t("dashboard_header_search_results", { count: resultCount() });
    return "";
  })();

  if (isMobile && !mobileOpen) {
    return (
      <div className={cn(styles.mobile, className)}>
        <button
          ref={triggerRef}
          type="button"
          className={styles.trigger}
          aria-label={t("dashboard_header_search_open")}
          aria-haspopup="dialog"
          aria-expanded="false"
          onClick={openMobile}
        >
          <SearchIcon aria-hidden="true" />
        </button>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn(styles.search, className, { [styles.fullScreen]: isFullScreen })}
      onBlur={handleBlur}
      role={isFullScreen ? "dialog" : undefined}
      aria-modal={isFullScreen ? "true" : undefined}
      aria-label={isFullScreen ? t("dashboard_header_search_label") : undefined}
    >
      <div className={styles.barRow}>
        <div className={cn(styles.bar, { [styles.barFocused]: open })}>
          <SearchIcon className={styles.searchIcon} aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            role="combobox"
            className={styles.input}
            aria-label={t("dashboard_header_search_label")}
            aria-expanded={open}
            aria-controls={open ? (showResults && hasRows ? listboxId : panelId) : undefined}
            aria-autocomplete="list"
            aria-activedescendant={open && activeIndex >= 0 ? optionId(activeIndex) : undefined}
            autoComplete="off"
            placeholder={t("dashboard_header_search_placeholder")}
            value={query}
            onChange={event => {
              setQuery(event.target.value);
              setView("main");
              openPanel();
            }}
            onFocus={openPanel}
            onClick={openPanel}
            onKeyDown={handleKeyDown}
          />
          {query !== "" ? (
            <button type="button" className={styles.clear} aria-label={t("dashboard_header_search_clear")} onClick={handleClear}>
              <CloseIcon aria-hidden="true" />
            </button>
          ) : null}
          <button
            type="button"
            className={styles.locationChip}
            aria-expanded={open && view === "location"}
            aria-label={t("dashboard_header_search_location_chip", { location: locationLabel || t("dashboard_header_search_anywhere") })}
            onClick={() => {
              openPanel();
              setView(view === "location" ? "main" : "location");
            }}
          >
            <LocationIcon className={styles.pin} aria-hidden="true" />
            <span className={styles.locationText}>{locationLabel || t("dashboard_header_search_anywhere")}</span>
            <span className={styles.caret} aria-hidden="true" />
          </button>
        </div>
        {isFullScreen ? (
          <button type="button" className={styles.cancel} onClick={closePanel}>
            {t("dashboard_header_search_close")}
          </button>
        ) : null}
      </div>
      <div className={styles.live} role="status" aria-live="polite">
        {announcement}
      </div>
      {open ? (
        <div id={panelId} className={styles.panel}>
          {view === "location" ? renderLocations() : null}
          {view === "main" && showResults ? renderResults() : null}
          {view === "main" && !showResults && status !== "error" && trimmed === "" ? renderIdle() : null}
          {view === "main" && !showResults && status === "loading" ? (
            <p className={styles.emptyText}>{t("dashboard_header_search_loading")}</p>
          ) : null}
          {view === "main" && status === "error" ? <p className={styles.emptyText}>{t("dashboard_header_search_error")}</p> : null}
        </div>
      ) : null}
    </div>
  );
};

const resultCountAll = counts => Object.values(counts).reduce((sum, count) => sum + (Number.isFinite(count) ? count : 0), 0);

export default MainSearchV2;
